import {useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Button, ButtonProps, Stack} from "@mui/material";
import {IEntity} from "@buildwitt/shapeshift-types/entities/IEntity";
// import {ILocalizationEntity} from "@buildwitt/shapeshift-types";
import {EntityIdentifier} from "@buildwitt/shapeshift-types/entityService";
import {ConfirmDeleteEntityDialog, ConfirmDeleteEntityDialogProps} from "@buildwitt/shapeshift-common/v2/admin";
import DeleteIcon from "@mui/icons-material/Delete";
import {useNavigate} from "react-router-dom";
import {AbandonUnsavedChangesDialog} from "@/pages/ManagerView/ContentCreation/AbandonUnsavedChangesDialog";

interface IPropsComponentOverrides<T extends IEntity & {title?: string}> {
	deleteDialog?: (e: ConfirmDeleteEntityDialogProps<T>) => JSX.Element,
	deleteButton?: (e: ButtonProps) => JSX.Element,
	cancelButton?: (e: ButtonProps) => JSX.Element,
	saveDraftButton?: (e: ButtonProps) => JSX.Element,
}

export interface IProps<T extends IEntity & {title?: string}, K extends EntityIdentifier> {
	children: JSX.Element | JSX.Element[],
	entity?: T,
	type: K,
	locale?: string,
	onLocaleChange?: (e: string) => void,
	onDelete: () => void,
	onSubmit: (publish?: boolean) => void,
	loading?: boolean,
	saveRequired: boolean,
	disablePublish?: boolean,
	removePublishOption?: boolean,
	rootSlug: string,
	componentOverrides?: IPropsComponentOverrides<T>
}

const useStyles = makeStyles()((theme) => ({
	adminFormContainer: {

	},
	body: {
		display: "grid",
		gridTemplateColumns: "repeat(12, 1fr)",
		gap: "16px"
	},
	formContainer: {
		gridColumn: "span 12",
		display: "flex",
		alignItems: "stretch",
		flexDirection: "column",
		gap: "24px"
		// padding: "24px",
		// flexGrow: 3
	},
	buttonContainer: {
		marginTop: theme.spacing(2),
		display: "flex",
		flexDirection: "column",
		gridColumn: "span 12",
		gap: "10px"
	},
	select: {
		width: "100%",
		marginTop: theme.spacing(2.5),
		"& fieldset": {
			borderColor: theme.palette.grey[200]
		},
		"& svg path": {
			fill: theme.palette.common.white
		}
	},
	deleteBtn: {
		alignSelf: "flex-start",
		color: theme.palette.error.main,
		borderColor: theme.palette.error.main,
		"&:hover": {
			backgroundColor: "transparent"
		}
	},
	buttonRow: {
		marginTop: theme.spacing(7),
		columnGap: theme.spacing(3),
		flexDirection: "row",
		display: "flex",
		alignItems: "center",
		flexWrap: "nowrap",
		width: "100%"
	},
	saveBtn: {
		lineHeight: 1,
		color: theme.palette.primary.main,
		"&:hover": {
			color: theme.palette.primary.contrastText
		}
	},
	cancelBtn: {
		border: "none"
	}
}));

export const AdminFormContainer = <T extends IEntity, K extends EntityIdentifier>({children, entity, rootSlug, componentOverrides, type, onDelete, onSubmit, disablePublish, saveRequired, removePublishOption}: IProps<T, K>): JSX.Element => {
	const {classes} = useStyles();
	// const [locales] = useApi<ILocalizationEntity[]>("/api/settings/i18n");
	const navigate = useNavigate();
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const isPublished = !!entity && !!entity.publishedAt
	// For new entities, allow publishing even if saveRequired is true (direct publish)
	// For existing entities, hide publish button if saveRequired is true (need to save changes first)
	const shouldShowPublishButton = !removePublishOption && (!saveRequired || !entity)

	const handleDeleteRequest = (): void => {
		setDeleteDialogOpen(true)
	}

	const handleCancel = (): void => {
		setDeleteDialogOpen(false)
	}

	const deleteDialogProps: ConfirmDeleteEntityDialogProps<T> = {
		onConfirm: onDelete,
		open: deleteDialogOpen,
		onClose: handleCancel,
		entity
	}

	const deleteButtonProps: ButtonProps = {
		className: classes.deleteBtn,
		startIcon: <DeleteIcon />,
		onClick: handleDeleteRequest
	}

	const cancelAction = (): void => navigate(entity?.id ? `${rootSlug}/${entity.id}` : `${rootSlug}`)

	const cancelHandler = (): void => {
		if (saveRequired) {
			setCancelDialogOpen(true)
			return;
		}
		cancelAction();
	}

	const cancelButtonProps: ButtonProps = {
		className: classes.cancelBtn,
		variant: "text",
		onClick: cancelHandler
	}

	const saveDraftButtonProps: ButtonProps = {
		variant: "outlined",
		className: classes.saveBtn,
		onClick: () => onSubmit()
	}

	return (
		<Box className={classes.adminFormContainer}>
			<Box className={classes.body}>
				<Stack className={classes.formContainer}>
					{children}
				</Stack>
				<Box className={classes.buttonContainer}>
					{componentOverrides?.deleteDialog ? componentOverrides?.deleteDialog(deleteDialogProps) : <ConfirmDeleteEntityDialog {...deleteDialogProps}/>}
					<AbandonUnsavedChangesDialog open={cancelDialogOpen} onConfirm={cancelAction} onClose={() => setCancelDialogOpen(false)} />
					<Stack className={classes.buttonRow}>
						{entity ? (componentOverrides?.deleteButton ? componentOverrides.deleteButton(deleteButtonProps) : <Button {...deleteButtonProps}>Delete {type}</Button>) : null}
						<Box sx={{ flexGrow: 1 }} /> {/* Spacer to push buttons to the right */}
						{!isPublished || !entity ? <>
							{componentOverrides?.cancelButton ? componentOverrides?.cancelButton(cancelButtonProps) : <Button {...cancelButtonProps}>Cancel</Button>}
							{componentOverrides?.saveDraftButton ? componentOverrides?.saveDraftButton(saveDraftButtonProps) : <Button {...saveDraftButtonProps}>Save as draft</Button>}
							{shouldShowPublishButton && <Button variant="contained" disabled={disablePublish} onClick={() => onSubmit(true)}>Publish now</Button>}
						</> : <>
							{componentOverrides?.cancelButton ? componentOverrides?.cancelButton(cancelButtonProps) : <Button {...cancelButtonProps}>Cancel</Button>}
							<Button variant="contained" onClick={() => onSubmit()}>Save changes</Button>
						</>}
					</Stack>
				</Box>
			</Box>
		</Box>
	);
}
